import {
    centsToFormattedDollars,
    convertDecimalToPercentageForInputDisplay,
    convertPercentageForInputDisplayToDecimal,
    formattedDollarsToCents,
    isEmpty,
} from '@fruitful/fruitils';
import {
    deriveRuleContributionType,
    getActualGoalPriorityFromSynthetic,
    getSyntheticGoalPriorityFromActual,
    stripMoneyMapAccountFromRules,
    updateRuleAndGroupPriorities,
} from '@fruitful/money-map';
import {
    AccountOwnership,
    type DeepPartial,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    MoneyMapRuleGoalType,
    type MoneyMapRuleGroup,
    MoneyMapRuleType,
    type MoneyMapRuleUpsertDto,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
    NEW_APEX_IM_ACCOUNT_OPTION,
    NEW_CASH_ACCOUNT_OPTION,
    NEW_IM_ACCOUNT_OPTION,
    PaycheckRoutingRuleType,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import {
    Controller,
    type SubmitHandler,
    useForm,
    type UseFormReturn,
} from 'react-hook-form';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    FootnoteRegular,
    Form,
    GoalIcon,
    Icon,
    StyledBodyMediumRegularInputNoOutline,
    USE_FORM_MODE_DEFAULTS,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { BACKDOOR_ROTH_ENABLED_IN_GROVE } from '../../../config/FruitfulEnvironment';
import { selectedMembershipAtom } from '../../../entities/entityAtoms';
import { useMoneyMapAccounts } from '../../providers/MoneyMapAccountsProvider';
import {
    getBackdoorRothPanelVisibility,
    getBackdoorRothPersonalizedAccountDefaults,
    resolveBackdoorRothFormAccounts,
    resolveExistingMoneyMapTransferAccountForDefaults,
} from '../../utils/backdoorRothFormShared';
import { getWhatThisMeansText } from '../../utils/goalEditorPanelUtils';
import { isAccountBackdoorTrad } from '../../utils/goalFormBackdoorRothUtils';
import {
    validateAccountNameLength,
    validateIndividualAccountOwnership,
    validatePhysicalCardLimits,
    validateRequiredFields,
    validateTransferAccountRowForMoneyMapRuleForm,
    validateTransferCashAccountFieldsWhenNotBackdoorRoth,
    validateTransferIndividualOwnershipUnlessBackdoorRoth,
    validateTransferPhysicalCardLimitsUnlessBackdoorRoth,
} from '../../utils/moneyMapAccountFormUtils';
import { validateTargetBalance } from '../../utils/moneyMapGoalFormUtils';
import { buildMoneyMapAccountForMoneyMapRuleUpsertDto } from '../../utils/moneyMapRuleUpsertDtoBuilder';
import {
    getDefaultAccountSettings,
    shouldUseTransferAccount,
} from '../../utils/moneyMapUtils';
import {
    AccountSettingsFormModule,
    accountSettingsFormSchema,
    type GenericAccountSettingsFormSchema,
    transferAccountSettingsFormSchema,
} from './AccountSettingsFormModule';
import { BackdoorRothAccountFields } from './BackdoorRothAccountFields';
import FormDrawer from './FormDrawer';
import {
    GoalSettingsFormModule,
    type GoalSettingsFormSchema,
    goalSettingsFormSchema,
} from './GoalSettingsFormModule';

const findExistingAccountForPresetAccount = (
    accounts: MoneyMapAccountWithAccountDetails[],
    accountPreset: DeepPartial<MoneyMapAccountWithAccountDetails> | undefined,
): MoneyMapAccountWithAccountDetails | undefined => {
    if (
        (accountPreset?.id === NEW_CASH_ACCOUNT_OPTION.value ||
            accountPreset?.id === NEW_IM_ACCOUNT_OPTION.value ||
            accountPreset?.id === NEW_APEX_IM_ACCOUNT_OPTION.value) &&
        accountPreset?.accountDetails?.name != null
    ) {
        // If referencing a proposed preset account, figure out if an account has already been saved via that preset.
        // Match on name (not 100% reliable) AND account type so that e.g. an old
        // INVESTMENT_MANAGEMENT Roth IRA isn't confused with a new APEX_INVESTMENT_MANAGEMENT one.
        return accounts.find(
            (a) =>
                a.type === accountPreset?.type &&
                a.accountDetails.name.toLowerCase() ===
                    accountPreset?.accountDetails?.name?.toLowerCase(),
        );
    }

    return undefined;
};

interface GoalFormProps {
    rule?: DeepPartial<MoneyMapRuleWithAccountDetails>;
    priority: number;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onPressSave: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<void>;
    onPressRemove: (ruleId?: string) => Promise<void>;
    rulePreset?: DeepPartial<MoneyMapRuleWithAccountDetails>;
    presetHelpText?: string;
    /** Which member the Backdoor Roth preset targets (0 = primary, 1 = partner). */
    backdoorRothUserIndex?: number;
    group?: DeepPartial<MoneyMapRuleGroup>;
    onClose?: () => void;
    presentation?: 'drawer' | 'modal' | 'inline';
}

const GoalForm = ({
    rule,
    priority,
    moneyMap,
    onPressSave,
    onPressRemove,
    rulePreset,
    presetHelpText,
    backdoorRothUserIndex = 0,
    group,
    onClose,
    presentation,
}: GoalFormProps) => {
    const { accounts } = useMoneyMapAccounts();
    const membership = useAtomValue(selectedMembershipAtom);
    const isSoloMembership = membership?.users?.length === 1;

    const backdoorTradAccountIds = useMemo(
        () => new Set(accounts.filter(isAccountBackdoorTrad).map((a) => a.id)),
        [accounts],
    );

    // Refer to rulePreset if rule does not exist to use rule preset defaults
    rule = rule ?? rulePreset;

    // Use group explicitly passed in via props, or query for existing group on rule
    group =
        group ??
        (moneyMap.moneyMapRuleGroups ?? []).find(
            (group) => group.id === rule?.moneyMapRuleGroupId,
        );

    // Extract defaults from account presets if they exist
    const { moneyMapAccount: accountPreset } = rulePreset ?? {};
    const isBackdoorRothPreset =
        BACKDOOR_ROTH_ENABLED_IN_GROVE &&
        Boolean(accountPreset?.backdoorRothLinkedMoneyMapAccount);

    const {
        backdoorSplitFromRule,
        primaryAccountForFormDefaults,
        transferAccountPreset,
    } = useMemo(
        () =>
            resolveBackdoorRothFormAccounts(
                rule?.moneyMapAccount,
                accountPreset,
                accounts,
            ),
        [rule?.moneyMapAccount, accountPreset, accounts],
    );

    // Primary "Account settings" is Roth when editing a backdoor pair (API stores Trad on the rule).
    const account = primaryAccountForFormDefaults ?? rule?.moneyMapAccount;

    const [
        showExternalAccountTargetBalanceWarning,
        setShowExternalAccountTargetBalanceWarning,
    ] = useState(false);

    const incomeAccount:
        | DeepPartial<MoneyMapAccountWithAccountDetails>
        | undefined = useMemo(() => {
        if (moneyMap?.incomeMoneyMapAccountId != null) {
            return accounts.find(
                (account) => account.id === moneyMap.incomeMoneyMapAccountId,
            );
        }

        return moneyMap.incomeMoneyMapAccount;
    }, [JSON.stringify(moneyMap)]);

    const existingAccountForTransfer = useMemo(
        () =>
            resolveExistingMoneyMapTransferAccountForDefaults({
                backdoorSplitFromRule,
                ruleMoneyMapAccount: rule?.moneyMapAccount,
                transferAccountPreset,
                accounts,
            }),
        [
            JSON.stringify(accounts),
            backdoorSplitFromRule,
            rule?.moneyMapAccount,
            transferAccountPreset,
        ],
    );

    // Define the form schema
    const goalFormSchema = z
        .object({
            ...accountSettingsFormSchema.shape,
            ...goalSettingsFormSchema.shape,
            ...transferAccountSettingsFormSchema.shape,
            isBackdoorRoth: z.boolean().optional(),
        })
        .superRefine(
            (
                {
                    accountSettings,
                    transferAccountSettings,
                    goalSettings,
                    isBackdoorRoth,
                },
                ctx,
            ) => {
                const isBackdoorRothGoal = isBackdoorRoth === true;
                const isNewCashAccount =
                    accountSettings.moneyMapAccountId ===
                    NEW_CASH_ACCOUNT_OPTION.value;
                const isNewIMAccount =
                    accountSettings.moneyMapAccountId ===
                    NEW_IM_ACCOUNT_OPTION.value;
                const isNewApexAccount =
                    accountSettings.moneyMapAccountId ===
                    NEW_APEX_IM_ACCOUNT_OPTION.value;

                const account = accounts.find(
                    (account) =>
                        account.id === accountSettings.moneyMapAccountId,
                );
                const transferAccount = accounts.find(
                    (account) =>
                        account.id ===
                        transferAccountSettings?.moneyMapAccountId,
                );

                const isProposedCashAccount =
                    account?.status === MoneyMapAccountStatus.PROPOSED &&
                    account?.type === MoneyMapAccountType.INTERNAL_BANKING;
                const isProposedIMAccount =
                    account?.status === MoneyMapAccountStatus.PROPOSED &&
                    account?.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT;
                const isProposedApexAccount =
                    account?.status === MoneyMapAccountStatus.PROPOSED &&
                    account?.type ===
                        MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT;

                const isTransferAccountRequired =
                    !isBackdoorRothGoal &&
                    shouldUseTransferAccount(
                        account,
                        accountSettings.moneyMapAccountId,
                        incomeAccount,
                    );
                const isTransferAccountValid =
                    validateTransferAccountRowForMoneyMapRuleForm(ctx, {
                        isBackdoorRothPair: isBackdoorRothGoal,
                        isIncomeSplitTransferRequired:
                            isTransferAccountRequired,
                        transferAccountSettings,
                        accountSettings,
                        transferAccount,
                    });

                const isAccountPhysicalCardValid = validatePhysicalCardLimits(
                    'account',
                    accounts,
                    isSoloMembership,
                    accountSettings,
                    account,
                    ctx,
                );

                const isTransferAccountPhysicalCardValid =
                    validateTransferPhysicalCardLimitsUnlessBackdoorRoth(
                        isBackdoorRothGoal,
                        accounts,
                        isSoloMembership,
                        transferAccountSettings,
                        transferAccount,
                        ctx,
                    );

                const isAccountOwnershipValid =
                    accountSettings.ownership === AccountOwnership.INDIVIDUAL
                        ? validateIndividualAccountOwnership(
                              'account',
                              accountSettings.primaryUserId,
                              ctx,
                          )
                        : true;

                const isTransferAccountOwnershipValid =
                    validateTransferIndividualOwnershipUnlessBackdoorRoth(
                        isBackdoorRothGoal,
                        transferAccountSettings,
                        ctx,
                    );

                const isNewOrProposedCashAccountValid =
                    isNewCashAccount || isProposedCashAccount
                        ? validateRequiredFields(
                              'account',
                              [
                                  ['name', accountSettings.name],
                                  ['ownership', accountSettings.ownership],
                                  ['category', accountSettings.category],
                                  ['virtualCard', accountSettings.virtualCard],
                                  [
                                      'physicalCard',
                                      accountSettings.physicalCard,
                                  ],
                              ],
                              ctx,
                          ) &&
                          validateAccountNameLength(
                              'account',
                              ctx,
                              accountSettings.name,
                          )
                        : true;

                const isNewOrProposedIMAccountValid =
                    isNewIMAccount || isProposedIMAccount
                        ? validateRequiredFields(
                              'account',
                              [
                                  ['name', accountSettings.name],
                                  ['ownership', accountSettings.ownership],
                                  [
                                      'investmentAccountType',
                                      accountSettings.investmentAccountType,
                                  ],
                                  ['portfolio', accountSettings.portfolio],
                                  ['riskLevel', accountSettings.riskLevel],
                              ],
                              ctx,
                          )
                        : true;

                const isNewOrProposedApexAccountValid =
                    isNewApexAccount || isProposedApexAccount
                        ? validateRequiredFields(
                              'account',
                              [
                                  ['name', accountSettings.name],
                                  ['ownership', accountSettings.ownership],
                                  [
                                      'apexInvestmentAccountType',
                                      accountSettings.apexInvestmentAccountType,
                                  ],
                                  [
                                      'apexPortfolio',
                                      accountSettings.apexPortfolio,
                                  ],
                                  [
                                      'apexRiskLevel',
                                      accountSettings.apexRiskLevel,
                                  ],
                                  [
                                      'apexTimeHorizon',
                                      accountSettings.apexTimeHorizon,
                                  ],
                              ],
                              ctx,
                          )
                        : true;

                const isNewOrProposedCashAccountForTransferValid =
                    validateTransferCashAccountFieldsWhenNotBackdoorRoth(
                        isBackdoorRothGoal,
                        transferAccountSettings,
                        transferAccount,
                        ctx,
                    );

                // Validate the target balance / loan balance.
                const shouldValidateTargetBalance =
                    goalSettings.goalType ===
                        (MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE as string) ||
                    goalSettings.goalType ===
                        (MoneyMapRuleGoalType.DEBT_PAY_DOWN as string);
                const isTargetBalanceValid = shouldValidateTargetBalance
                    ? validateTargetBalance(goalSettings.targetBalance, ctx)
                    : true;

                // Show warning for goal type of build up target balance for external accounts (since their current balances cannot be reliably known)
                // This is intentionally excluded as a custom error on the zod context since that would invalidate the whole schema
                const invalidGoalTypeForAccount =
                    goalSettings.goalType ===
                        (MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE as string) &&
                    (account?.type === MoneyMapAccountType.EXTERNAL_HOLISTIC ||
                        account?.type ===
                            MoneyMapAccountType.EXTERNAL_BANKING ||
                        account?.type ===
                            MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT);
                setShowExternalAccountTargetBalanceWarning(
                    invalidGoalTypeForAccount,
                );

                return (
                    isTransferAccountValid &&
                    isAccountPhysicalCardValid &&
                    isTransferAccountPhysicalCardValid &&
                    isAccountOwnershipValid &&
                    isTransferAccountOwnershipValid &&
                    isNewOrProposedCashAccountValid &&
                    isNewOrProposedIMAccountValid &&
                    isNewOrProposedApexAccountValid &&
                    isNewOrProposedCashAccountForTransferValid &&
                    isTargetBalanceValid
                );
            },
        );

    type GoalFormSchema = z.infer<typeof goalFormSchema>;

    const goalFormDefaultValues = useMemo(() => {
        const personalized = getBackdoorRothPersonalizedAccountDefaults(
            membership,
            backdoorRothUserIndex,
        );

        const accountSettingsSource =
            account ??
            findExistingAccountForPresetAccount(accounts, accountPreset) ??
            accountPreset;

        const accountSettingsMaybe = getDefaultAccountSettings(
            accountSettingsSource,
        );
        if (accountSettingsMaybe == null) {
            throw new Error(
                'Primary account settings must be defined for goal form defaults',
            );
        }
        let accountSettings = accountSettingsMaybe;

        const transferSettingsSource =
            existingAccountForTransfer ??
            findExistingAccountForPresetAccount(
                accounts,
                transferAccountPreset,
            ) ??
            transferAccountPreset;

        let transferAccountSettings = getDefaultAccountSettings(
            transferSettingsSource,
            true,
        );

        if (isBackdoorRothPreset && personalized != null) {
            if (
                accountSettings.moneyMapAccountId ===
                NEW_APEX_IM_ACCOUNT_OPTION.value
            ) {
                accountSettings = {
                    ...accountSettings,
                    name: personalized.rothAccountName,
                    primaryUserId:
                        accountSettings.primaryUserId ||
                        personalized.primaryUserId,
                };
            }
            if (
                transferAccountSettings?.moneyMapAccountId ===
                NEW_APEX_IM_ACCOUNT_OPTION.value
            ) {
                transferAccountSettings = {
                    ...transferAccountSettings,
                    name: personalized.tradAccountName,
                    primaryUserId:
                        transferAccountSettings.primaryUserId ||
                        personalized.primaryUserId,
                };
            }
        }

        return {
            goalSettings: {
                priority: getSyntheticGoalPriorityFromActual(
                    priority,
                    moneyMap,
                ).toString(),
                description:
                    rule?.description ??
                    (isBackdoorRothPreset ? 'Backdoor Roth' : ''),
                goalType: rule?.goalType ?? '',
                contributionType:
                    deriveRuleContributionType(rule, group != null) ?? '',
                targetBalance:
                    rule?.targetBalanceCents != null
                        ? centsToFormattedDollars(
                              rule.targetBalanceCents,
                              false,
                          )
                        : '',
                percentage:
                    rule?.percentage != null
                        ? convertDecimalToPercentageForInputDisplay(
                              rule.percentage,
                          )
                        : '',
                monthlyContributionMax:
                    rule?.monthlyContributionMaxCents != null
                        ? centsToFormattedDollars(
                              rule.monthlyContributionMaxCents,
                              false,
                          )
                        : '',
                yearlyContributionMax:
                    rule?.yearlyContributionMaxCents != null
                        ? centsToFormattedDollars(
                              rule.yearlyContributionMaxCents,
                              false,
                          )
                        : '',
            },
            accountSettings,
            transferAccountSettings,
            isBackdoorRoth: backdoorSplitFromRule.isBackdoorRoth,
        };
    }, [
        account,
        accountPreset,
        accounts,
        backdoorRothUserIndex,
        backdoorSplitFromRule.isBackdoorRoth,
        existingAccountForTransfer,
        group,
        isBackdoorRothPreset,
        membership,
        moneyMap,
        priority,
        rule,
        transferAccountPreset,
        BACKDOOR_ROTH_ENABLED_IN_GROVE,
    ]);

    /** Existing backdoor rules: UI is suppressed in production but form state must keep transfer + isBackdoorRoth for save. */
    const preserveTransferWhenBackdoorUiSuppressed =
        !BACKDOOR_ROTH_ENABLED_IN_GROVE && backdoorSplitFromRule.isBackdoorRoth;

    const formFunctions = useForm<GoalFormSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(goalFormSchema),
        defaultValues: goalFormDefaultValues,
    });

    const rothPrimaryUserId = formFunctions.watch(
        'accountSettings.primaryUserId',
    );
    const isBackdoorRothForm = formFunctions.watch('isBackdoorRoth');
    const transferMoneyMapIdForOwnerSync = formFunctions.watch(
        'transferAccountSettings.moneyMapAccountId',
    );

    useEffect(() => {
        if (isBackdoorRothForm !== true) {
            return;
        }
        const isNewSentinel =
            transferMoneyMapIdForOwnerSync === NEW_APEX_IM_ACCOUNT_OPTION.value;
        const isProposedTrad =
            !isNewSentinel &&
            accounts.find((a) => a.id === transferMoneyMapIdForOwnerSync)
                ?.status === MoneyMapAccountStatus.PROPOSED;
        if (!isNewSentinel && !isProposedTrad) {
            return;
        }
        if (rothPrimaryUserId != null && rothPrimaryUserId !== '') {
            formFunctions.setValue(
                'transferAccountSettings.primaryUserId',
                rothPrimaryUserId,
                { shouldValidate: true },
            );
        }
    }, [
        accounts,
        formFunctions,
        isBackdoorRothForm,
        rothPrimaryUserId,
        transferMoneyMapIdForOwnerSync,
    ]);

    const selectedMoneyMapAccountId = formFunctions.watch(
        'accountSettings.moneyMapAccountId',
    );

    const selectedMoneyMapAccount = useMemo(() => {
        if (
            selectedMoneyMapAccountId != null &&
            selectedMoneyMapAccountId !== NEW_CASH_ACCOUNT_OPTION.value &&
            selectedMoneyMapAccountId !== NEW_IM_ACCOUNT_OPTION.value &&
            selectedMoneyMapAccountId !== NEW_APEX_IM_ACCOUNT_OPTION.value
        ) {
            return accounts.find(
                (account) => account.id === selectedMoneyMapAccountId,
            );
        }

        return findExistingAccountForPresetAccount(accounts, accountPreset);
    }, [JSON.stringify(accounts), accountPreset, selectedMoneyMapAccountId]);

    const selectedMoneyMapAccountIdForTransfer = formFunctions.watch(
        'transferAccountSettings.moneyMapAccountId',
    );

    const selectedMoneyMapAccountForTransfer = useMemo(() => {
        return accounts.find(
            (account) => account.id === selectedMoneyMapAccountIdForTransfer,
        );
    }, [JSON.stringify(accounts), selectedMoneyMapAccountIdForTransfer]);

    // Build a notice when the selected account is also referenced by other rules
    // (within or across other sets), so members understand which goals share an account.
    const sharedAccountNotice = useMemo(() => {
        if (
            selectedMoneyMapAccountId == null ||
            isEmpty(selectedMoneyMapAccountId) ||
            selectedMoneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value ||
            selectedMoneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value ||
            selectedMoneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value
        ) {
            return null;
        }

        const otherGoalsSharingAccount = (moneyMap.moneyMapRules ?? []).filter(
            (otherRule) =>
                otherRule?.id !== rule?.id &&
                otherRule?.moneyMapAccountId === selectedMoneyMapAccountId,
        );

        if (otherGoalsSharingAccount.length === 0) {
            return null;
        }

        const distinctGroupIds = new Set(
            otherGoalsSharingAccount.map(
                (otherRule) =>
                    otherRule?.moneyMapRuleGroupId ?? '__ungrouped__',
            ),
        );
        // Include the current rule's own group/ungrouped bucket in the comparison
        // so the notice only renders when the same account is referenced by more
        // than one set.
        distinctGroupIds.add(
            rule?.moneyMapRuleGroupId ?? group?.id ?? '__ungrouped__',
        );

        if (distinctGroupIds.size < 2) {
            return null;
        }

        const otherGoalDescriptions = otherGoalsSharingAccount
            .map((otherRule) => otherRule?.description?.trim())
            .filter(
                (description): description is string =>
                    description != null && description !== '',
            );

        if (otherGoalDescriptions.length === 0) {
            return null;
        }

        const joinedGoalNames =
            otherGoalDescriptions.length === 1
                ? otherGoalDescriptions[0]
                : otherGoalDescriptions.length === 2
                  ? `${otherGoalDescriptions[0]} and ${otherGoalDescriptions[1]}`
                  : `${otherGoalDescriptions.slice(0, -1).join(', ')}, and ${otherGoalDescriptions[otherGoalDescriptions.length - 1]}`;

        const goalNoun = otherGoalDescriptions.length === 1 ? 'goal' : 'goals';

        return `This goal uses the same account as your ${joinedGoalNames} ${goalNoun}`;
    }, [
        selectedMoneyMapAccountId,
        moneyMap.moneyMapRules,
        rule?.id,
        rule?.moneyMapRuleGroupId,
        group?.id,
    ]);

    const onSubmit: SubmitHandler<GoalFormSchema> = async ({
        goalSettings,
        accountSettings,
        transferAccountSettings,
        isBackdoorRoth,
    }) => {
        if (accountSettings.moneyMapAccountId == null) return;

        const proposedAccountToUpdate = accounts.find(
            (account) =>
                account.id === accountSettings.moneyMapAccountId &&
                account.status === MoneyMapAccountStatus.PROPOSED,
        );
        const proposedTransferAccountToUpdate = accounts.find(
            (account) =>
                account.id === transferAccountSettings?.moneyMapAccountId &&
                account.status === MoneyMapAccountStatus.PROPOSED,
        );

        const updatedRule: MoneyMapRuleUpsertDto = {
            id: rule?.id,
            moneyMapId: moneyMap?.id,
            type: MoneyMapRuleType.GOAL,
            goalType: goalSettings.goalType as MoneyMapRuleGoalType,
            moneyMapRuleGroupId: group?.id,
            description: goalSettings.description,
            priority: getActualGoalPriorityFromSynthetic(
                parseInt(goalSettings.priority),
                moneyMap,
            ),
            routingRuleType: !isEmpty(goalSettings.targetBalance)
                ? PaycheckRoutingRuleType.MINIMUM_BALANCE
                : PaycheckRoutingRuleType.PERCENTAGE,
            targetBalanceCents: !isEmpty(goalSettings.targetBalance)
                ? formattedDollarsToCents(goalSettings.targetBalance as string)
                : null,
            percentage: !isEmpty(goalSettings.percentage)
                ? convertPercentageForInputDisplayToDecimal(
                      goalSettings.percentage as string,
                  )
                : null,
            monthlyContributionMaxCents: !isEmpty(
                goalSettings.monthlyContributionMax,
            )
                ? formattedDollarsToCents(
                      goalSettings.monthlyContributionMax as string,
                  )
                : null,
            yearlyContributionMaxCents: !isEmpty(
                goalSettings.yearlyContributionMax,
            )
                ? formattedDollarsToCents(
                      goalSettings.yearlyContributionMax as string,
                  )
                : null,
        };

        const updatedRuleWithAccount =
            buildMoneyMapAccountForMoneyMapRuleUpsertDto(
                updatedRule,
                { accountSettings },
                { transferAccountSettings },
                MoneyMapCategory.GOALS,
                membership,
                selectedMoneyMapAccount,
                proposedAccountToUpdate,
                proposedTransferAccountToUpdate,
                incomeAccount,
                { isBackdoorRoth: isBackdoorRoth === true },
                selectedMoneyMapAccountForTransfer,
            );

        // Exclude existing moneyMapAccount dto object from upsert call.
        const existingRules = stripMoneyMapAccountFromRules(
            moneyMap.moneyMapRules ?? [],
        );

        // Update priorities across all rules and groups
        const { rules: updatedRules, groups: updatedGroups } =
            updateRuleAndGroupPriorities(
                updatedRuleWithAccount,
                existingRules,
                moneyMap.moneyMapRuleGroups ?? [],
                'upsert',
            );

        await onPressSave({
            moneyMapRules: updatedRules,
            moneyMapRuleGroups: updatedGroups,
        });
    };

    const handleSubmit = formFunctions.handleSubmit;
    const isValid = formFunctions.formState.isValid;
    const goalNameError =
        formFunctions.formState.errors.goalSettings?.description;

    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleSave = handleSubmit(async (values) => {
        setIsSaving(true);
        try {
            await onSubmit(values);
        } finally {
            setIsSaving(false);
        }
    });

    const handleDelete =
        rule?.id != null
            ? async () => {
                  setIsDeleting(true);
                  try {
                      await onPressRemove(rule.id);
                  } finally {
                      setIsDeleting(false);
                  }
              }
            : undefined;

    const isBackdoorRothWatch = formFunctions.watch('isBackdoorRoth');
    const apexRothTypeWatch = formFunctions.watch(
        'accountSettings.apexInvestmentAccountType',
    );

    // Show the transfer account settings if using a preset and the money map account has not yet been specified.
    const shouldShowTransferAccount =
        !isBackdoorRothWatch &&
        ((transferAccountPreset != null &&
            isEmpty(selectedMoneyMapAccountId)) ||
            shouldUseTransferAccount(
                selectedMoneyMapAccount,
                selectedMoneyMapAccountId,
                incomeAccount,
            ));

    const {
        shouldShowBackdoorCheckbox,
        shouldShowBackdoorTradPanel,
        shouldDisableBackdoorCheckbox,
        shouldForceBackdoorRothChecked,
        isKnownBackdoorRoth,
    } = getBackdoorRothPanelVisibility({
        isBackdoorRothWatch,
        selectedMoneyMapAccountId,
        selectedMoneyMapAccount,
        apexRothTypeWatch,
        accounts,
    });

    // Reset transferAccountSettings when neither transfer panel is shown
    useEffect(() => {
        if (preserveTransferWhenBackdoorUiSuppressed) {
            return;
        }
        if (!shouldShowTransferAccount && !shouldShowBackdoorTradPanel) {
            formFunctions.setValue('transferAccountSettings', undefined);
        }
    }, [
        preserveTransferWhenBackdoorUiSuppressed,
        shouldShowTransferAccount,
        shouldShowBackdoorTradPanel,
        formFunctions,
    ]);

    // Re-apply default transfer settings when the backdoor Trad panel is shown again after being cleared
    // (unchecking backdoor clears transfer state; RHF does not re-run defaultValues on re-check).
    useEffect(() => {
        if (!shouldShowBackdoorTradPanel) {
            return;
        }
        const transfer = formFunctions.getValues('transferAccountSettings');
        const needsHydration =
            transfer == null ||
            isEmpty(transfer) ||
            transfer.moneyMapAccountId == null ||
            transfer.moneyMapAccountId === '';
        if (!needsHydration) {
            return;
        }
        const defaults = goalFormDefaultValues.transferAccountSettings;
        if (defaults != null) {
            formFunctions.setValue('transferAccountSettings', defaults, {
                shouldValidate: true,
                shouldDirty: false,
            });
        }
    }, [
        shouldShowBackdoorTradPanel,
        goalFormDefaultValues.transferAccountSettings,
        formFunctions,
    ]);

    useEffect(() => {
        if (!BACKDOOR_ROTH_ENABLED_IN_GROVE) {
            return;
        }
        if (!shouldShowBackdoorCheckbox && isBackdoorRothWatch) {
            formFunctions.setValue('isBackdoorRoth', false);
        }
    }, [
        BACKDOOR_ROTH_ENABLED_IN_GROVE,
        shouldShowBackdoorCheckbox,
        isBackdoorRothWatch,
        formFunctions,
    ]);

    useEffect(() => {
        if (!isKnownBackdoorRoth) {
            return;
        }
        if (shouldForceBackdoorRothChecked && !isBackdoorRothWatch) {
            formFunctions.setValue('isBackdoorRoth', true, {
                shouldValidate: true,
            });
        }
        const pairedTrad = accounts.find(
            (a) =>
                a.backdoorRothLinkedMoneyMapAccountId ===
                selectedMoneyMapAccountId,
        );
        if (pairedTrad != null) {
            const currentTransferId = formFunctions.getValues(
                'transferAccountSettings.moneyMapAccountId',
            );
            if (currentTransferId !== pairedTrad.id) {
                const tradDefaults = getDefaultAccountSettings(
                    pairedTrad,
                    true,
                );
                formFunctions.setValue(
                    'transferAccountSettings',
                    tradDefaults,
                    { shouldValidate: true },
                );
            }
        }
    }, [
        isKnownBackdoorRoth,
        shouldForceBackdoorRothChecked,
        isBackdoorRothWatch,
        formFunctions,
        accounts,
        selectedMoneyMapAccountId,
    ]);

    // Trigger form validation.
    useEffect(() => {
        const subscription = formFunctions.watch(() => {
            void formFunctions.trigger(); // Re-run validation including superRefine
        });

        // Initial trigger for selected account IDs
        void formFunctions.trigger();

        return () => {
            subscription.unsubscribe();
        };
    }, [
        formFunctions,
        selectedMoneyMapAccountId,
        selectedMoneyMapAccountIdForTransfer,
        isBackdoorRothWatch,
        shouldForceBackdoorRothChecked,
    ]);

    return (
        <FormDrawer
            onClose={onClose}
            presentation={presentation}
            onSave={() => {
                void handleSave();
            }}
            saveDisabled={!isValid}
            onDelete={
                handleDelete != null
                    ? () => {
                          void handleDelete();
                      }
                    : undefined
            }
            isSaving={isSaving}
            isDeleting={isDeleting}
        >
            <Form onSubmit={handleSave}>
                <YStack gap={16}>
                    <YStack gap={4}>
                        <Controller
                            control={formFunctions.control}
                            name="goalSettings.description"
                            render={({
                                field: { onChange, onBlur, value, ref },
                            }) => (
                                <StyledBodyMediumRegularInputNoOutline
                                    ref={ref}
                                    value={value ?? ''}
                                    onChangeText={onChange}
                                    onBlur={onBlur}
                                    placeholder={presetHelpText ?? 'Goal name'}
                                    placeholderTextColor={
                                        goalNameError != null
                                            ? '$red600'
                                            : '$gray800'
                                    }
                                    color={
                                        goalNameError != null
                                            ? '$red1100'
                                            : '$gray1200'
                                    }
                                    textAlign="left"
                                    fontSize={24}
                                    paddingHorizontal={0}
                                    invalid={goalNameError != null}
                                />
                            )}
                        />
                        {goalNameError?.message != null && (
                            <FootnoteRegular color="$red1100">
                                {goalNameError.message}
                            </FootnoteRegular>
                        )}
                    </YStack>
                    <GoalSettingsFormModule
                        formFunctions={
                            formFunctions as never as UseFormReturn<GoalSettingsFormSchema>
                        }
                        showExternalAccountTargetBalanceWarning={
                            showExternalAccountTargetBalanceWarning
                        }
                        belongsToGroup={group != null}
                        isInvestmentManagementAccount={
                            selectedMoneyMapAccountId ===
                                NEW_APEX_IM_ACCOUNT_OPTION.value ||
                            selectedMoneyMapAccount?.type ===
                                MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT ||
                            selectedMoneyMapAccount?.type ===
                                MoneyMapAccountType.INVESTMENT_MANAGEMENT
                        }
                    />
                    {presentation === 'inline' && (
                        <XStack
                            backgroundColor="$orchidPink300"
                            borderRadius={16}
                            padding={16}
                            gap={12}
                            alignItems="center"
                        >
                            <Icon icon={GoalIcon} color="$orchidPink1100" />
                            <YStack flex={1} gap={2}>
                                <BodyMediumEmphasized color="$orchidPink1100">
                                    What this means:
                                </BodyMediumEmphasized>
                                <BodyMediumRegular
                                    color="$orchidPink1100"
                                    fontStyle="italic"
                                >
                                    {getWhatThisMeansText({
                                        ...rule,
                                        goalType: formFunctions.watch(
                                            'goalSettings.goalType',
                                        ) as MoneyMapRuleGoalType,
                                        targetBalanceCents:
                                            formattedDollarsToCents(
                                                formFunctions.watch(
                                                    'goalSettings.targetBalance',
                                                ) ?? '',
                                            ) || 0,
                                        monthlyContributionMaxCents:
                                            formattedDollarsToCents(
                                                formFunctions.watch(
                                                    'goalSettings.monthlyContributionMax',
                                                ) ?? '',
                                            ) || 0,
                                        yearlyContributionMaxCents:
                                            formattedDollarsToCents(
                                                formFunctions.watch(
                                                    'goalSettings.yearlyContributionMax',
                                                ) ?? '',
                                            ) || 0,
                                    })}
                                </BodyMediumRegular>
                            </YStack>
                        </XStack>
                    )}
                    <AccountSettingsFormModule
                        type="account"
                        accounts={accounts}
                        selectedAccount={selectedMoneyMapAccount}
                        formFunctions={
                            formFunctions as never as UseFormReturn<GenericAccountSettingsFormSchema>
                        }
                        isUsingPreset={
                            moneyMap.isAIGenerated === true ||
                            (accountPreset != null &&
                                selectedMoneyMapAccount == null)
                        }
                        ruleType={MoneyMapRuleType.GOAL} // Manually pass this in since a new rule may not have type set.
                        disabledAccountIds={backdoorTradAccountIds}
                        bottomNotice={sharedAccountNotice}
                    />
                    <BackdoorRothAccountFields
                        accounts={accounts}
                        formFunctions={
                            formFunctions as never as UseFormReturn<
                                GenericAccountSettingsFormSchema & {
                                    isBackdoorRoth?: boolean;
                                }
                            >
                        }
                        ruleType={MoneyMapRuleType.GOAL}
                        shouldShowBackdoorCheckbox={shouldShowBackdoorCheckbox}
                        shouldShowBackdoorTradPanel={
                            shouldShowBackdoorTradPanel
                        }
                        shouldDisableBackdoorCheckbox={
                            shouldDisableBackdoorCheckbox
                        }
                        shouldForceBackdoorRothChecked={
                            shouldForceBackdoorRothChecked
                        }
                        selectedMoneyMapAccount={selectedMoneyMapAccount}
                        selectedMoneyMapAccountForTransfer={
                            selectedMoneyMapAccountForTransfer
                        }
                        isUsingPresetTransfer={
                            moneyMap.isAIGenerated === true ||
                            (transferAccountPreset != null &&
                                selectedMoneyMapAccount == null)
                        }
                    />
                    {shouldShowTransferAccount && (
                        <AccountSettingsFormModule
                            type="transferAccount"
                            accounts={accounts}
                            selectedAccount={selectedMoneyMapAccountForTransfer}
                            formFunctions={
                                formFunctions as never as UseFormReturn<GenericAccountSettingsFormSchema>
                            }
                            isUsingPreset={
                                moneyMap.isAIGenerated === true ||
                                (transferAccountPreset != null &&
                                    selectedMoneyMapAccount == null)
                            }
                            ruleType={MoneyMapRuleType.GOAL} // Manually pass this in since a new rule may not have type set.
                        />
                    )}
                </YStack>
            </Form>
        </FormDrawer>
    );
};

export default GoalForm;
